import { useQuery } from '@tanstack/react-query'
import { Button, StateBlock } from '@w7/ui'
import { useState } from 'react'
import { cmApi } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { canViewBetfairRatesLogIp } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'

export function formatRate(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return String(n)
}

export function BetfairRatesLogsPage() {
  const me = useCmMe()
  const showIp = me.isSuccess && canViewBetfairRatesLogIp(me.data?.caller)
  const [page, setPage] = useState(1)
  const pageSize = 50

  const logsQ = useQuery({
    queryKey: ['cm', 'betfair-rates-logs', page, pageSize],
    queryFn: () => cmApi.listBetfairRatesLogs(authHeaders(), { page, pageSize }),
  })

  const logs = logsQ.data?.logs ?? []
  const total = logsQ.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  if (logsQ.isLoading && !logs.length) {
    return (
      <StateBlock icon="activity" title="Loading Betfair Rates logs…">
        Reading Lab BetfairRatesLogs via CM-API.
      </StateBlock>
    )
  }

  return (
    <CmPanel
      title="Betfair Rates Logs"
      subtitle="Lab BetfairRatesLogs — who changed TM place % / min stake, from which IP"
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={logsQ.isFetching}
          onClick={() => void logsQ.refetch()}
        >
          Refresh
        </Button>
      }
    >
      {logsQ.isError ? (
        <div className="cm-hint" role="alert">
          <p>{(logsQ.error as Error).message}</p>
          <Button
            type="button"
            variant="secondary"
            busy={logsQ.isFetching}
            onClick={() => void logsQ.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      {!logs.length && !logsQ.isLoading && !logsQ.isError ? (
        <p className="cm-hint">No Volume Percent changes logged yet. Save Volume Percent to create entries.</p>
      ) : (
        <div className="cm-table-wrap">
          <table className="cm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>When</th>
                <th>From</th>
                <th>To</th>
                <th>Game</th>
                <th>Percent</th>
                <th>Old Percent</th>
                <th>Min Bet Stack</th>
                <th>Old Min Bet Stack</th>
                {showIp ? <th>IP</th> : null}
              </tr>
            </thead>
            <tbody>
              {logs.map((row, i) => (
                <tr key={row.id}>
                  <td>{(page - 1) * pageSize + i + 1}</td>
                  <td>
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="w7-truncate" title={row.parentUsername || undefined}>
                    {row.parentUsername || '—'}
                  </td>
                  <td>{row.username || '—'}</td>
                  <td>{row.game || '—'}</td>
                  <td>{formatRate(row.percent)}</td>
                  <td>{formatRate(row.oldPercent)}</td>
                  <td>{formatRate(row.minBet)}</td>
                  <td>{formatRate(row.oldMinBet)}</td>
                  {showIp ? <td>{row.ip ?? '—'}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > pageSize ? (
        <div className="cm-actions">
          <Button
            type="button"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="cm-hint">
            Page {page} / {totalPages} · {total} total
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      ) : null}
    </CmPanel>
  )
}
